import { type ResourceItemDTO, type ResourceHeaderDTO, type UnitDTO } from '@/src/ui';

export const filterSlidesData = (
    grade: string,
    slidesData: ResourceItemDTO[],
    language?: string
): ResourceItemDTO[] => {
    //GK Spanish: filter out all slides
    if (grade === 'K' && language === 'ES') {
        return [];
    }

    switch (grade) {
        case 'K':
            return slidesData.filter((item) => {
                return item.key && (item.key.includes('Unit6') || item.key.includes('Unit8'));
            });
        case '1':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit3') ||
                        item.key.includes('Unit5') ||
                        item.key.includes('Unit11'))
                );
            });
        case '2':
            return slidesData.filter((item) => item.key && item.key.includes('Unit5'));
        case '3':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit6') ||
                        item.key.includes('Unit7') ||
                        item.key.includes('Unit8'))
                );
            });
        case '4':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit5') ||
                        item.key.includes('Unit6') ||
                        item.key.includes('Unit7'))
                );
            });
        case '5':
            return slidesData.filter((item) => {
                return (
                    item.key &&
                    (item.key.includes('Unit5') ||
                        item.key.includes('Unit6') ||
                        item.key.includes('Unit8'))
                );
            });
        case '6':
            return slidesData.filter((item) => item.key && item.key.includes('Unit10'));
        case '6M':
            return slidesData.filter((item) => {
                return item.key && !item.key.includes('Editable');
            });
        case '7':
            return slidesData.filter((item) => {
                return item.key && !item.key.includes('Editable');
            });
        case '8':
        // Allow all
        // falls through
        default:
            return slidesData;
    }
};

export const filterUnitsData = (grade: string, unitsData: UnitDTO[], language?: string): UnitDTO[] => {
    let allowedUnits = [];
    switch (grade) {
        case 'K':
        case '1':
        case '2':
            allowedUnits = ['5', '6', '7', '8'];
            break;
        case '3':
            allowedUnits = ['1', '2', '3', '4', '5', '6', '7'];
            break;
        case '4':
            allowedUnits = ['1', '2', '4', '5', '6', '7'];
            break;
        case '5':
            allowedUnits = ['1', '2', '5', '6', '7'];
            break;
        case '6':
        case '6M':
            allowedUnits = ['1', '2', '5', '6', '7'];
            break;
        case '7':
            allowedUnits = ['1', '2', '3', '4', '5'];
            break;
        case '8':
            allowedUnits = ['1', '2', '3', '4'];
            break;
        default:
            return unitsData;
    }

    let filteredUnitsData = unitsData.map((unit) => {
        if (!allowedUnits.includes(`${unit.unitNumber}`)) {
            unit.lessons = unit.lessons.map((lesson) => {
                if (!lesson.subitems) return lesson;

                lesson.subitems = lesson.subitems.filter((subitem) => {
                    return subitem.type && subitem.type !== 'Lesson Video';
                });

                return lesson;
            });
        }
        return unit;
    });

    //GK Spanish: filter out Strategy Videos and Lesson Videos
    if (grade === 'K' && language === 'ES') {
        filteredUnitsData = filteredUnitsData.map((unit) => {
            unit.lessons = unit.lessons.map((lesson) => {
                if (!lesson.subitems) return lesson;
                lesson.subitems = lesson.subitems.filter(
                    (subitem) => subitem.type !== 'Strategy Video' && subitem.type !== 'Lesson Video'
                );
                return lesson;
            });
            return unit;
        });
    }

    return filteredUnitsData;
};

export const filterResourcesData = (assessmentsData: ResourceHeaderDTO[], grade: string, language?: string): ResourceHeaderDTO[] => {
    let newResourcesData = [...assessmentsData];
    function removeActivityResources() {
        return newResourcesData.map((item: ResourceHeaderDTO) => {
            if (item.key === 'BTBFBox') {
                const fluencyWorksheets: ResourceItemDTO | undefined = item.items?.find(
                    (subitem: ResourceItemDTO) => subitem.text === 'Fluency Worksheets'
                );
                if (fluencyWorksheets) {
                    fluencyWorksheets.subitems = fluencyWorksheets.subitems?.filter(
                        (subitem: ResourceItemDTO) => !subitem.text.includes('Online Practice')
                    );
                }
            }
            return item;
        });
    }

    newResourcesData = removeActivityResources();

    function removeExemplarBox() {
        return newResourcesData.filter((item: ResourceHeaderDTO) => item.key !== 'exemplarsBox');
    }

    newResourcesData = removeExemplarBox();

    //GK Spanish Resources
    if (grade === 'K' && language === 'ES') {

        //Filters out Calendar Routines
        function removeSpanishCalendarRoutines() {
            return newResourcesData.map((item: ResourceHeaderDTO) => {
                item.items = item.items?.filter(
                    (subitem: ResourceItemDTO) => subitem.key !== 'CalendarRoutines'
                );
                return item;
            });
        }

        newResourcesData = removeSpanishCalendarRoutines();

        //Filters out Family Letters
        function removeSpanishFamilyLetters() {
            return newResourcesData.map((item: ResourceHeaderDTO) => {
                item.items = item.items?.filter(
                    (subitem: ResourceItemDTO) => subitem.key !== 'TeacherEditionFamilyLetters'
                );
                return item;
            });
        }

        newResourcesData = removeSpanishFamilyLetters();

        //Filters out BTBF Fluency Worksheets, but keeps allowed ones
        function removeSpanishBTBFFluencyWorksheets() {
            const allowedSpanishFluencyItems = ['Suma Hasta 5', 'Resta Hasta 5'];
            return newResourcesData.map((item: ResourceHeaderDTO) => {
                if (item.key === 'BTBFBox') {
                    const spanishFluencyWorksheets = item.items?.find(
                        (subitem: ResourceItemDTO) => subitem.key === 'BTBFFluencyWorksheets'
                    );
                    if (spanishFluencyWorksheets) {
                        spanishFluencyWorksheets.subitems = spanishFluencyWorksheets.subitems?.filter(
                            (subitem: ResourceItemDTO) =>
                                allowedSpanishFluencyItems.includes(subitem.text)
                        );
                    }
                }
                return item;
            });
        }

        newResourcesData = removeSpanishBTBFFluencyWorksheets();

        //Filters out Additional Resources Box
        function removeAdditionalResourcesBox() {
            return newResourcesData.filter((item: ResourceHeaderDTO) => item.key !== 'additionalResourcesBox');
        }

        newResourcesData = removeAdditionalResourcesBox();
    }

    return newResourcesData;
};

export const filterAssessmentsData = (
    assessmentsData: ResourceHeaderDTO[],
    grade: string,
    language?: string
): ResourceHeaderDTO[] => {
    let newAssessmentData = [...assessmentsData];
    function removeOnlineAssessments() {
        return newAssessmentData.filter(
            (item: ResourceHeaderDTO) => item.key !== 'onlineAssessmentsBox'
        );
    }
    function removeActivitiesByStandard() {
        return newAssessmentData.filter(
            (item: ResourceHeaderDTO) => item.key !== 'activitesByStandardBox'
        );
    }

    newAssessmentData = removeOnlineAssessments();
    newAssessmentData = removeActivitiesByStandard();

    //GK Spanish Assessments
    if (grade === 'K' && language === 'ES') {

        //Filters out Open Ended Pretests (Preliminar X RA)
        function removeOpenEndedPretests() {
            return newAssessmentData.map((item: ResourceHeaderDTO) => {
                if (item.key === 'PretestsBox') {
                    item.items = item.items?.map((pretest: ResourceItemDTO) => {
                        pretest.subitems = pretest.subitems?.filter(
                            (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                        );
                        return pretest;
                    });
                }
                return item;
            });
        }

        newAssessmentData = removeOpenEndedPretests();

        //Filters out Open Ended Unit Tests (Unidad X RA)
        function removeOpenEndedUnitTests() {
            return newAssessmentData.map((item: ResourceHeaderDTO) => {
                if (item.key === 'unitTestsBox') {
                    item.items = item.items?.map((version: ResourceItemDTO) => {
                        version.subitems = version.subitems?.filter(
                            (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                        );
                        return version;
                    });
                }
                return item;
            });
        }

        newAssessmentData = removeOpenEndedUnitTests();

        //Filters out Open Ended Benchmarks (Evaluaciones de Referencia X RA)
        function removeOpenEndedBenchmarks() {
            return newAssessmentData.map((item: ResourceHeaderDTO) => {
                if (item.key === 'BenchmarksBox') {
                    item.items = item.items?.map((version: ResourceItemDTO) => {
                        version.subitems = version.subitems?.filter(
                            (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                        );
                        return version;
                    });
                }
                return item;
            });
        }

        newAssessmentData = removeOpenEndedBenchmarks();

        //Filters out Open Ended Answer Keys (Claves de Respuestas RA)
        function removeOpenEndedAnswerKeys() {
            return newAssessmentData.map((item: ResourceHeaderDTO) => {
                if (item.key === 'answerKeysBox') {
                    item.items = item.items?.filter(
                        (subitem: ResourceItemDTO) => !subitem.link?.includes('_OE')
                    );
                }
                return item;
            });
        }

        newAssessmentData = removeOpenEndedAnswerKeys();
    }

    return newAssessmentData;
};

export const filterLanguageSetting = (grade: string, language: string): string => {
    if (['K', '3', '6'].includes(grade)) {
        return language;
    }
    return 'EN';
};
